import {
  CachedValue,
  RTLObserver
} from "@ingka/webc-tools";
import { SkapaElement } from "@ingka/skapa-webc-element";
import { CarouselControls } from "./elements/CarouselControls.js";
import { SlideVisibilityFeature } from "./features/slide-visibility.js";
import { SlideIndexFeature } from "./features/slide-index.js";
import { PagingControlsFeature } from "./features/paging-controls.js";
import { ChildFocusFeature } from "./features/child-focus-handler.js";
import { TouchModeDetectionFeature } from "./features/touch-mode-detection.js";
import { ResizeHandlerFeature } from "./features/resize-handler.js";
import { ScrollHandlerFeature } from "./features/scroll-handler.js";
import { RTLHandlerFeature } from "./features/rtl-handler.js";
import { AutoSlideSizeFeature } from "./features/auto-slide-size.js";
const baseFeatures = {
  scroller: ScrollHandlerFeature,
  slideVisibility: SlideVisibilityFeature,
  slideIndex: SlideIndexFeature,
  resizeHandler: ResizeHandlerFeature,
  pagingControls: PagingControlsFeature,
  childFocusHandler: ChildFocusFeature,
  touchModeDetection: TouchModeDetectionFeature,
  writingDirection: RTLHandlerFeature,
  autoSlideSize: AutoSlideSizeFeature
};
class CarouselBase extends SkapaElement {
  constructor(templateGenerator) {
    super();
    // Cache until slotchange event.
    this.__assignedChildren = new CachedValue(() => {
      return this.slides.assignedElements({ flatten: true }).filter((node) => node.nodeType === 1);
    });
    // Slot assigned children have been updated
    this.handleSlotChange = () => {
      this.__assignedChildren.updateCache = true;
      this.features.scroller.updateScrollState();
    };
    /**
     *
     * @param slide Reference to the element to be displayed.
     * @param offsetPercent Number between 0 and 1: 0 will align the left edge of the slide and the container, 1 will align it to the right.
     * @param padding Number interpreted in pixels. Will offset the alignment with the value, creating a padding in respect of the container.
     */
    this.scrollToSlide = (slide, offsetPercent = 0, padding = 0) => {
      const target = slide.offsetLeft - (this.clientWidth - slide.clientWidth) * offsetPercent - (1 - 2 * offsetPercent) * padding;
      this.features.scroller.scrollLeft = target;
    };
    const templates = this.getTemplates(templateGenerator);
    const shadow = this.shadow = this.attachShadow({ mode: "open" });
    shadow.append(templates.main.content.cloneNode(true));
    this.slides = shadow.querySelector("#slides");
    this.scrollContainer = shadow.querySelector(".wrapper");
    this.controls = shadow.querySelector(".controls");
    this.features = Object.fromEntries(
      Object.entries(baseFeatures).map(
        ({ 0: featureName, 1: featureClass }) => [
          featureName,
          new featureClass(this)
        ]
      )
    );
    this.featuresList = Object.values(this.features);
  }
  // Observed attributes are defined within the features.
  static get observedAttributes() {
    const featureAttributes = Object.values(baseFeatures).map((feature) => feature.observedAttributes).flat();
    return featureAttributes;
  }
  // Get slot assigned HTMLElements
  get assignedChildren() {
    return this.__assignedChildren.value;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.featuresList.forEach(
      (feature) => feature.attributeChangedCallback(name, oldValue, newValue)
    );
  }
  // Override: Forward the scrollLeft controls to the embedded scrollContainer
  set scrollLeft(value) {
    if (this.scrollContainer)
      this.scrollContainer.scrollLeft = value;
  }
  // Override: Forward the scrollLeft controls to the embedded scrollContainer
  get scrollLeft() {
    return this.scrollContainer ? this.scrollContainer.scrollLeft : 0;
  }
  // Override: Forward the scrollWidth controls to the embedded scrollContainer
  get scrollWidth() {
    return this.scrollContainer ? this.scrollContainer.scrollWidth : this.clientWidth;
  }
  connectedCallback() {
    super.connectedCallback();
    RTLObserver.addTarget(this);
    this.featuresList.forEach((feature) => feature.connectedCallback());
    this.slides.addEventListener("slotchange", this.handleSlotChange);
    this.scrollContainer.addEventListener("dragstart", cancelEvent);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    RTLObserver.removeTarget(this);
    this.slides.removeEventListener("slotchange", this.handleSlotChange);
    this.featuresList.forEach((feature) => feature.disconnectedCallback());
    this.scrollContainer.removeEventListener("dragstart", cancelEvent);
  }
}
CarouselBase.skapaDependencies = [
  CarouselControls
];
function cancelEvent(ev) {
  ev.preventDefault();
}
export {
  CarouselBase
};
